import { Box, Button, Paper, Popover, TextField, Typography } from "@mui/material";
import { useTheme } from "@mui/material/styles";
import dayjs, { Dayjs } from "dayjs";
import * as React from "react";
import { DateRange, DayPicker } from "react-day-picker";

interface CustomDatetimeRangePickerProps {
  value?: [Dayjs | null, Dayjs | null];
  onChange?: (value: [Dayjs | null, Dayjs | null] | null) => void;
  status?: "error";
  label?: string;
}

export default function CustomDatetimeRangePicker({
  value,
  onChange,
  status,
  label = "Choose range",
}: CustomDatetimeRangePickerProps) {
  const theme = useTheme();

  //
  const [range, setRange] = React.useState<DateRange | undefined>(
    value?.[0] && value?.[1] ? { from: value[0].toDate(), to: value[1].toDate() } : undefined
  );
  const [fromTime, setFromTime] = React.useState<Dayjs | null>(value?.[0] ?? null);
  const [toTime, setToTime] = React.useState<Dayjs | null>(value?.[1] ?? null);

  const [anchorEl, setAnchorEl] = React.useState<HTMLElement | null>(null);
  const open = Boolean(anchorEl);

  //
  React.useEffect(() => {
    if (!value) {
      setRange(undefined);
      setFromTime(null);
      setToTime(null);
      return;
    }

    setRange(
      value[0] && value[1] ? { from: value[0].toDate(), to: value[1].toDate() } : undefined
    );
    setFromTime(value[0]);
    setToTime(value[1]);
  }, [value]);

  const mergeDateTime = (date?: Date, time?: Dayjs | null) =>
    date
      ? dayjs(date)
          .hour(time?.hour() ?? 0)
          .minute(time?.minute() ?? 0)
      : null;

  const fromDateTime = mergeDateTime(range?.from, fromTime);
  const toDateTime = mergeDateTime(range?.to, toTime);

  const isValid = fromDateTime && toDateTime && !toDateTime.isBefore(fromDateTime);

  const displayValue =
    fromDateTime && toDateTime
      ? `${fromDateTime.format("DD.MM.YYYY HH:mm")} — ${toDateTime.format("DD.MM.YYYY HH:mm")}`
      : "";

  //

  const handleOpen = (e: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(e.currentTarget);
  };

  const handleCancel = () => {
    // rollback to the latest submitted value
    if (value?.[0] && value?.[1]) {
      setRange({ from: value[0].toDate(), to: value[1].toDate() });
      setFromTime(value[0]);
      setToTime(value[1]);
    } else {
      setRange(undefined);
      setFromTime(null);
      setToTime(null);
    }
    setAnchorEl(null);
  };

  const handleApply = () => {
    if (!isValid) return;
    onChange?.([fromDateTime!, toDateTime!]);
    setAnchorEl(null);
  };

  const handleFromTimeChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    if (!e.target.value) {
      setFromTime(null);
      return;
    }
    const t = dayjs(`2000-01-01 ${e.target.value}`);
    setFromTime(t);
    //
    if (
      range?.from &&
      range?.to &&
      dayjs(range.from).isSame(dayjs(range.to), "day") &&
      toTime &&
      toTime.isBefore(t)
    ) {
      setToTime(t);
    }
  };

  const handleToTimeChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    if (!e.target.value) {
      setToTime(null);
      return;
    }
    const t = dayjs(`2000-01-01 ${e.target.value}`);

    if (
      range?.from &&
      range?.to &&
      dayjs(range.from).isSame(dayjs(range.to), "day") &&
      fromTime &&
      t.isBefore(fromTime)
    ) {
      return;
    }

    setToTime(t);
  };

  //
  const toMinTime =
    range?.from && range?.to && dayjs(range.from).isSame(dayjs(range.to), "day") && fromTime
      ? fromTime.format("HH:mm")
      : undefined;

  const rangeBg =
    theme.palette.mode === "dark" ? "rgba(25, 118, 210, 0.35)" : "rgba(25, 118, 210, 0.18)";

  return (
    <>
      <TextField
        label={label}
        value={displayValue}
        onClick={handleOpen}
        inputProps={{ readOnly: true }}
        size="small"
        fullWidth
        error={status === "error"}
      />

      <Popover
        open={open}
        anchorEl={anchorEl}
        onClose={handleCancel}
        anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
      >
        <Paper
          sx={{
            p: 1.5,
            width: 320,
            bgcolor: "background.paper",

            "& .rdp-root": {
              "--rdp-range_middle-background-color": rangeBg,
              "--rdp-range_middle-color": theme.palette.text.primary,
              "--rdp-accent-color": theme.palette.primary.main,
              "--rdp-accent-background-color": theme.palette.action.hover,
              "--rdp-day_button-border-radius": "999px",
              "--rdp-day_button-height": "32px",
              "--rdp-day_button-width": "32px",
            } as React.CSSProperties,

            "& .rdp": {
              color: "text.primary",
              fontSize: 12,
            },

            "& .rdp-head_cell": {
              color: "text.secondary",
            },

            "& .rdp-day_disabled": {
              color: "text.disabled",
            },
          }}
        >
          <DayPicker
            mode="range"
            selected={range}
            onSelect={setRange}
            numberOfMonths={1}
            weekStartsOn={1}
          />

          {range?.from && (
            <Box display="flex" gap={1} mt={1}>
              <TextField
                type="time"
                label="From"
                size="small"
                value={fromTime?.format("HH:mm") ?? ""}
                onChange={handleFromTimeChange}
                fullWidth
                inputProps={{ step: 60 }}
              />

              <TextField
                type="time"
                label="To"
                size="small"
                value={toTime?.format("HH:mm") ?? ""}
                onChange={handleToTimeChange}
                fullWidth
                inputProps={{ step: 60, min: toMinTime }}
              />
            </Box>
          )}

          {fromDateTime && toDateTime && (
            <Typography variant="caption" sx={{ fontSize: 11, mt: 1, display: "block" }}>
              {fromDateTime.format("DD.MM.YYYY HH:mm")} —{" "}
              {toDateTime.format("DD.MM.YYYY HH:mm")}
            </Typography>
          )}

          <Box mt={1.5} display="flex" justifyContent="flex-end" gap={1}>
            <Button size="small" onClick={handleCancel}>
              Cancel
            </Button>
            <Button size="small" variant="contained" onClick={handleApply} disabled={!isValid}>
              Apply
            </Button>
          </Box>
        </Paper>
      </Popover>
    </>
  );
}
